// frontend/src/services/socket.js - FIXED PARAMETER HANDLING
import io from 'socket.io-client';
import { store } from '../store';
import {
  addMessage, setOnlineUsers, setUserOnlineStatus,
  setTyping, markMessagesAsRead, revokeMessage
} from '../store/slices/messagesSlice';
import { updateOrderStatus } from '../store/slices/ordersSlice';
import notify from '../utils/notify';

const getSocketURL = () => {
  if (import.meta.env.VITE_SOCKET_URL) return import.meta.env.VITE_SOCKET_URL;
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
  }
  if (import.meta.env.DEV) return 'http://localhost:5000';
  // Bug #6: Production must have VITE_SOCKET_URL or VITE_API_URL set
  throw new Error('VITE_SOCKET_URL and VITE_API_URL are not set. Cannot initialize socket in production.');
};

const SOCKET_URL = getSocketURL();

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.connectionAttempted = false;
    this.lastToken = null;
    this.lastVerifiedState = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * ✅ FIXED: Connect with flexible parameter handling
   */
  connect(isBasicVerifiedOrToken, tokenOrUndefined) {
    // ✅ Handle both old and new calling patterns
    let isBasicVerified;
    let token;

    if (typeof isBasicVerifiedOrToken === 'string') {
      // Old pattern: connect(token)
      token = isBasicVerifiedOrToken;
      isBasicVerified = true; // Assume verified if token provided
      console.log('🔄 Using legacy connect pattern (token only)');
    } else {
      // New pattern: connect(isBasicVerified, token)
      isBasicVerified = isBasicVerifiedOrToken;
      token = tokenOrUndefined;
    }

    // Validate inputs
    if (!isBasicVerified) {
      console.log('⛔ User not basic verified. Aborting socket connect.');
      this.disconnect();
      return;
    }

    if (!token) {
      console.log('⚠️ No token supplied. Aborting socket connect.');
      this.disconnect();
      return;
    }

    // Get fresh token from localStorage if available
    const freshToken = localStorage.getItem('gigship_token') || token;

    // Avoid duplicate connections
    if (this.socket?.connected &&
      this.lastToken === freshToken &&
      this.lastVerifiedState === isBasicVerified) {
      console.log('🔄 Socket already connected with same credentials. Skipping.');
      return;
    }

    // If a socket exists but token changed → reconnect fresh
    if (this.socket) {
      console.log('🔄 Changing token or state → reconnecting socket.');
      this.disconnect();
    }

    console.log('🔌 Connecting socket →', SOCKET_URL);

    try {
      this.socket = io(SOCKET_URL, {
        auth: { token: freshToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      this.connectionAttempted = true;
      this.lastToken = freshToken;
      this.lastVerifiedState = isBasicVerified;

      // ✅ CRITICAL: Validate socket instance
      if (!this.socket || typeof this.socket.on !== 'function') {
        console.error('❌ Invalid socket instance. Aborting.');
        this.socket = null;
        this.isConnected = false;
        return;
      }

      this.setupEventListeners();
    } catch (error) {
      console.error('❌ Socket initialization failed:', error);
      this.socket = null;
      this.isConnected = false;
    }
  }

  reconnect(newToken, state = true) {
    console.log('🔄 Forced reconnect.');
    this.reconnectAttempts = 0;
    this.connect(state, newToken);
  }

  disconnect() {
    if (this.socket) {
      try {
        console.log('🔌 Disconnecting socket...');
        this.socket.disconnect();
      } catch (err) {
        console.warn('⚠️ Socket disconnect error:', err);
      }
    }
    this.socket = null;
    this.isConnected = false;
    this.connectionAttempted = false;
    this.lastToken = null;
    this.lastVerifiedState = false;
    this.reconnectAttempts = 0;
  }

  safeOn(event, handler) {
    if (!this.socket || typeof this.socket.on !== 'function') {
      console.warn(`⚠️ Skipped listener "${event}" → socket not ready`);
      return;
    }
    try {
      this.socket.on(event, handler);
    } catch (e) {
      console.error(`❌ Failed attaching listener "${event}":`, e);
    }
  }

  /** Public proxy — callers must never touch this.socket directly */
  on(event, handler) { this.safeOn(event, handler); }

  off(event, handler) {
    if (!this.socket) return;
    try {
      this.socket.off(event, handler);
    } catch (e) {
      console.error(`❌ Failed removing listener "${event}":`, e);
    }
  }

  setupEventListeners() {
    if (!this.socket || typeof this.socket.on !== 'function') {
      console.error('❌ Cannot setup listeners: socket invalid');
      return;
    }


    // Core connection events
    this.safeOn('connect', () => {
      console.log('🟢 Socket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      notify?.success?.('Connected', 'Realtime connection established');
    });

    this.safeOn('disconnect', (reason) => {
      console.log('🔴 Socket disconnected:', reason);
      this.isConnected = false;

      if (reason === 'io server disconnect') {
        console.log('🔄 Server disconnected, attempting reconnect...');
        const token = localStorage.getItem('gigship_token');
        if (token && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => {
            this.connect(true, token);
          }, 2000);
        }
      }

      notify?.warning?.('Disconnected', reason || 'Lost connection');
    });

    this.safeOn('connect_error', (error) => {
      console.error('❌ Socket connection error:', error?.message);
      this.isConnected = false;

      if ((error?.message || '').toLowerCase().includes('auth')) {
        notify?.error?.('Auth Error', 'Please login again');
        localStorage.removeItem('gigship_token');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        notify?.error?.('Connection Error', 'Realtime server unavailable');
      }
    });

    // Online users
    this.safeOn('online_users', (users) => {
      store?.dispatch?.(setOnlineUsers(users));
    });

    this.safeOn('user_status_change', ({ userId, isOnline }) => {
      store?.dispatch?.(setUserOnlineStatus({ userId, isOnline }));
    });

    // Messaging
    this.safeOn('message_received', ({ message }) => {
      if (!message) return;
      // addMessage reducer expects the raw message as payload; inject conversationId
      const enriched = {
        ...message,
        conversationId: message.conversationId || message.conversation,
      };
      store?.dispatch?.(addMessage(enriched));
      const state = store.getState?.();
      const current = state?.messages?.currentConversation;
      if (!current || current !== enriched.conversationId) {
        notify?.info?.(
          'New Message',
          `${message.sender?.username || 'Someone'}: ${message.content?.text?.substring(0, 50) || '...'}`
        );
      }
    });

    this.safeOn('typing_indicator', (payload) => {
      store?.dispatch?.(setTyping(payload));
    });

    this.safeOn('messages_read', ({ conversationId }) => {
      store?.dispatch?.(markMessagesAsRead({ conversationId }));
    });

    this.safeOn('message_revoked', (payload) => {
      if (!payload?.messageId) return;
      store?.dispatch?.(revokeMessage(payload));
      notify?.warning?.('Message Revoked', 'A voice message was removed for policy violation');
    });

    // Orders
    this.safeOn('order_update', ({ orderId, update }) => {
      store?.dispatch?.(updateOrderStatus({ orderId, ...update }));
      const important = ['active', 'delivered', 'completed', 'cancelled'];
      if (important.includes(update?.status)) {
        notify?.info?.('Order Update', `Order now: ${update.status}`);
      }
    });

    // Notifications
    this.safeOn('notification', ({ title, message, type }) => {
      notify?.custom?.(type || 'info', title, message);
    });

    this.safeOn('system_announcement', ({ title, message, type }) => {
      notify?.custom?.(type || 'info', title, message, 10000);
    });
  }

  emit(event, data) {
    if (!this.socket || !this.socket.connected) {
      console.warn(`⚠️ Emit skipped (${event}) — socket not connected`);
      return;
    }
    try {
      this.socket.emit(event, data);
    } catch (e) {
      console.error(`❌ Emit failed (${event}):`, e);
    }
  }

  // Helper methods
  joinConversation(id) { this.emit('join_conversation', id); }
  leaveConversation(id) { this.emit('leave_conversation', id); }
  sendMessage(cid, content, type = 'text', attachments = []) {
    this.emit('send_message', { conversationId: cid, content, messageType: type, attachments });
  }
  startTyping(cid) { this.emit('typing_start', { conversationId: cid }); }
  stopTyping(cid) { this.emit('typing_stop', { conversationId: cid }); }
  markAsRead(cid) { this.emit('mark_read', { conversationId: cid }); }
  joinOrder(id) { this.emit('join_order', id); }
  leaveOrder(id) { this.emit('leave_order', id); }
  isSocketConnected() { return !!(this.socket?.connected); }
}

const socketService = new SocketService();
export default socketService;